import { useEffect, useState } from "react";
import { 
  Shield, 
  RefreshCw, 
  Download, 
  ChevronDown, 
  ChevronRight, 
  AlertCircle, 
  CheckCircle2, 
  AlertTriangle,
  User,
  Clock,
  Building,
  Tag
} from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import api, { getProperties } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";

export default function AdminAuditTrail() {
  const [logs, setLogs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("all");
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    setPage(1);
    fetchLogs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, typeFilter, levelFilter]);

  const fetchProperties = async () => {
    try {
      const data = await getProperties();
      setProperties(data);
    } catch (error) {
      console.error("Error fetching properties:", error);
    }
  };

  const fetchLogs = async (reset = false) => {
    setLoading(true);
    try {
      const currentPage = reset ? 1 : page;
      const limit = 50;
      const skip = (currentPage - 1) * limit;
      
      let params = { limit, skip };
      if (selectedProperty !== "all") params.property_id = selectedProperty;
      if (typeFilter !== "all") params.target_type = typeFilter;
      if (levelFilter !== "all") params.level = levelFilter;

      const response = await api.get("/audit-logs", { params });
      const newLogs = response.data;
      
      if (reset) {
        setLogs(newLogs);
      } else {
        setLogs((prev) => [...prev, ...newLogs]);
      }
      
      setHasMore(newLogs.length === limit);
      if (!reset) setPage(currentPage + 1);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      toast.error("Failed to load audit trail");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (logs.length === 0) {
      toast.error("No logs to export");
      return;
    }
    
    const headers = ["Timestamp", "Property", "User", "Role", "Action", "Target", "Old Value", "New Value", "Level"];
    const rows = logs.map(log => [
      new Date(log.timestamp).toLocaleString(),
      log.property_id || "Global",
      log.actor_username,
      log.actor_role,
      log.action,
      log.target_label || log.target_id,
      JSON.stringify(log.old_value || {}),
      JSON.stringify(log.new_value || {}),
      log.level
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(e => e.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `audit_trail_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const getLevelBadge = (level) => {
    switch (level) {
      case "error": 
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[11px] font-semibold rounded-full border border-red-200 shrink-0">
            <AlertCircle className="w-3 h-3 text-red-500" />
            Error
          </span>
        );
      case "warning": 
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-semibold rounded-full border border-amber-200 shrink-0">
            <AlertTriangle className="w-3 h-3 text-amber-500" />
            Warning
          </span>
        );
      default: 
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full border border-emerald-200 shrink-0">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            Info
          </span>
        );
    }
  };

  const formatAction = (action) => {
    if (!action) return "Action";
    return action.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatCategory = (category) => {
    if (!category) return "General";
    return category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (isToday) {
      return `Today, ${timeStr}`;
    }
    const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr}, ${timeStr}`;
  };

  const getOneLiner = (log) => {
    if (log.new_value?.error) {
      const err = String(log.new_value.error);
      return err.length > 90 ? `${err.slice(0, 90)}...` : err;
    }
    if (log.action === "settings_updated") {
      const keys = Object.keys(log.new_value || {}).filter(k => k !== "updated_at");
      if (keys.length > 0) {
        return `Updated: ${keys.slice(0, 3).join(", ")}${keys.length > 3 ? ` +${keys.length - 3} more` : ""}`;
      }
      return "Property settings saved";
    }
    if (log.action === "templates_synced_with_global") {
      return "All message templates & SIDs synced with Global master";
    }
    if (log.action?.includes("status") && log.new_value?.status) {
      return `Status changed to: ${log.new_value.status}`;
    }
    if (log.new_value && typeof log.new_value === "object") {
      const keys = Object.keys(log.new_value).filter(k => k !== "updated_at" && k !== "created_at");
      if (keys.length > 0) {
        return `Modified ${keys.slice(0, 3).join(", ")}${keys.length > 3 ? ` +${keys.length - 3}` : ""}`;
      }
    }
    return `${log.target_label || log.target_id || log.target_type || "Resource"}`;
  };

  return (
    <AdminLayout>
      <div data-testid="admin-audit-trail" className="space-y-6">
        {/* Header & Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-[#264653] flex items-center gap-3">
              <Shield className="w-7 h-7 text-[#2A9D8F]" />
              Audit Trail
            </h1>
            <p className="text-sm text-[#6B705C] mt-0.5">High-density activity feed of all system, admin, and staff actions</p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-40 sm:w-48 h-9 text-xs rounded-xl border-[#E8E4D9]">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Properties</SelectItem>
                <SelectItem value="global">Global (Platform)</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.property_id} value={p.property_id}>
                    {p.property_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 h-9 text-xs rounded-xl border-[#E8E4D9]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="setting">Settings</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
                <SelectItem value="menu_item">Menu</SelectItem>
                <SelectItem value="order">Orders</SelectItem>
                <SelectItem value="experience">Experiences</SelectItem>
                <SelectItem value="user">Users</SelectItem>
              </SelectContent>
            </Select>

            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-32 h-9 text-xs rounded-xl border-[#E8E4D9]">
                <SelectValue placeholder="All Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              onClick={() => fetchLogs(true)} 
              variant="outline" 
              size="sm"
              className="h-9 px-3 rounded-xl border-[#E8E4D9] text-[#264653]"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Refresh
            </Button>

            <Button 
              onClick={downloadCSV} 
              size="sm"
              className="h-9 px-3.5 rounded-xl bg-[#264653] hover:bg-[#1d3540] text-white"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Export
            </Button>
          </div>
        </div>

        {/* Content Feed */}
        {loading && logs.length === 0 ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-14 bg-white rounded-xl border border-[#E8E4D9] p-4 flex items-center gap-4">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E4D9]">
            <Shield className="w-12 h-12 text-[#E8E4D9] mx-auto mb-3" />
            <p className="text-base font-medium text-[#264653]">No audit records found</p>
            <p className="text-xs text-[#6B705C] mt-1">Try selecting a different property, category, or level filter.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => {
              const isExpanded = expandedId === log.id;
              const hasDiff = 
                (log.old_value && Object.keys(log.old_value).length > 0) || 
                (log.new_value && Object.keys(log.new_value).length > 0);
              const oneLiner = getOneLiner(log);

              return (
                <div 
                  key={log.id} 
                  className={`border rounded-xl transition-all overflow-hidden ${
                    isExpanded 
                      ? "border-[#2A9D8F] shadow-md bg-white" 
                      : "border-[#E8E4D9] bg-white hover:border-[#2A9D8F]/60 hover:shadow-sm"
                  }`}
                >
                  {/* High-density Straight Bar Row */}
                  <div 
                    onClick={() => toggleExpand(log.id)}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:px-4 gap-2.5 cursor-pointer hover:bg-[#FBF9F6]/60 transition-colors select-none"
                  >
                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5 min-w-0 flex-1">
                      {/* Level Badge */}
                      {getLevelBadge(log.level)}

                      {/* Property Pill */}
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md bg-[#264653]/10 text-[#264653] shrink-0 font-mono uppercase tracking-tight">
                        {log.property_id || "GLOBAL"}
                      </span>

                      {/* Category Tag */}
                      <span className="text-[11px] px-2 py-0.5 rounded-md bg-[#E8E4D9]/60 text-[#6B705C] shrink-0 font-medium">
                        {formatCategory(log.target_type)}
                      </span>

                      {/* Action Title */}
                      <span className="font-semibold text-xs sm:text-sm text-[#264653] shrink-0">
                        {formatAction(log.action)}
                      </span>

                      {/* Actor */}
                      <span className="text-xs text-[#6B705C] hidden md:inline shrink-0">
                        by <span className="font-medium text-[#2A9D8F]">{log.actor_username || "system"}</span>
                        {log.actor_role && <span className="text-[11px] text-gray-400 ml-1">({log.actor_role})</span>}
                      </span>

                      {/* One-Liner Summary */}
                      <span 
                        className={`text-xs truncate flex-1 min-w-0 ${
                          log.level === "error" ? "text-red-600 font-mono font-medium" : "text-[#6B705C]"
                        }`}
                        title={oneLiner}
                      >
                        • {oneLiner}
                      </span>
                    </div>

                    {/* Timestamp & Expand Arrow */}
                    <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto text-xs text-[#6B705C]">
                      <span className="font-mono text-[11px] sm:text-xs text-gray-500 whitespace-nowrap">
                        {formatTime(log.timestamp)}
                      </span>
                      <div className="w-5 h-5 rounded-full bg-[#E8E4D9]/40 flex items-center justify-center text-[#264653]">
                        {isExpanded ? (
                          <ChevronDown className="w-3.5 h-3.5 text-[#2A9D8F]" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Detail Drawer */}
                  {isExpanded && (
                    <div className="border-t border-[#E8E4D9] bg-[#FBF9F6] p-4 text-xs space-y-4 animate-in fade-in-50 duration-150">
                      {/* Metadata Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-white rounded-lg border border-[#E8E4D9]/80 text-[#264653]">
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider">Exact Timestamp</span>
                          <span className="font-mono text-xs">{new Date(log.timestamp).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider">Actor / User</span>
                          <span className="font-medium text-xs text-[#2A9D8F]">
                            {log.actor_username || "system"} {log.actor_role && `(${log.actor_role})`}
                          </span>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider">Property</span>
                          <span className="font-medium text-xs">{log.property_id || "Global (Platform)"}</span>
                        </div>
                        <div>
                          <span className="text-[10px] uppercase font-bold text-gray-400 block tracking-wider">Target Resource</span>
                          <span className="font-medium text-xs">{log.target_label || log.target_id || log.target_type || "N/A"}</span>
                        </div>
                      </div>

                      {/* Error Highlight if Error */}
                      {log.new_value?.error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="text-red-800 font-bold mb-1 flex items-center gap-1.5">
                            <AlertCircle className="w-4 h-4 text-red-600" />
                            Detailed Error Description:
                          </div>
                          <pre className="text-red-700 font-mono text-xs whitespace-pre-wrap break-all bg-white p-2.5 rounded border border-red-100">
                            {String(log.new_value.error)}
                          </pre>
                        </div>
                      )}

                      {/* Diff Inspector (Old vs New) */}
                      {hasDiff && !log.new_value?.error && (
                        <div className="bg-white rounded-lg border border-[#E8E4D9] p-3">
                          <div className="font-semibold text-[#264653] mb-2">Change Inspector (Old vs New):</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {log.old_value && Object.keys(log.old_value).length > 0 && (
                              <div className="bg-red-50/40 rounded-lg p-2.5 border border-red-100">
                                <span className="text-red-700 font-bold text-[11px] uppercase tracking-wider block mb-1.5">
                                  Before (Old Value)
                                </span>
                                <pre className="font-mono text-[11px] text-gray-700 whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
                                  {JSON.stringify(log.old_value, null, 2)}
                                </pre>
                              </div>
                            )}

                            {log.new_value && Object.keys(log.new_value).length > 0 && (
                              <div className="bg-emerald-50/40 rounded-lg p-2.5 border border-emerald-100">
                                <span className="text-emerald-700 font-bold text-[11px] uppercase tracking-wider block mb-1.5">
                                  After (New Value)
                                </span>
                                <pre className="font-mono text-[11px] text-gray-700 whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
                                  {JSON.stringify(log.new_value, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {hasMore && !loading && (
          <div className="mt-6 text-center">
            <Button 
              onClick={() => fetchLogs()} 
              variant="outline" 
              className="rounded-xl border-[#E8E4D9] text-[#264653] text-xs h-9 px-6 hover:bg-[#2A9D8F]/10"
            >
              Load Older Logs
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
